import { AlertTriangle } from "lucide-react";

import { LAB_ORDER_STATUS } from "features/labOrders/constants";
import { deriveLabOrderStatus } from "features/labOrders/utils/labOrderStatus";

import s from "./LabOrderRejectionNotice.module.css";

// Why an order did not go through. Two very different cases:
//
//   Removed from the work list — the laboratory accepted the order and then pulled
//   it. The specification requires the reason it gave to reach the GP, so it is the
//   headline here, never a technical detail.
//
//   Rejected by the receiving system — a defect in the message. The GP gets the
//   plain-language error; the code and the field position stay available for
//   support, collapsed, because they are meaningless at the point of care.
export const LabOrderRejectionNotice = ({ order }) => {
  const status = deriveLabOrderStatus(order);

  if (status === LAB_ORDER_STATUS.removedFromWorklist) {
    return (
      <section className={s.notice} role="alert">
        <AlertTriangle className={s.icon} size={20} aria-hidden="true" />
        <div className={s.body}>
          <h2 className={s.title}>
            The laboratory removed this order from its work list
          </h2>
          {order.rejectionReason ? (
            <blockquote className={s.reason}>{order.rejectionReason}</blockquote>
          ) : (
            <p className={s.text}>
              The laboratory gave no reason. Contact the laboratory quoting the
              order reference below.
            </p>
          )}
        </div>
      </section>
    );
  }

  if (status !== LAB_ORDER_STATUS.rejected) return null;

  const hasTechnicalDetail =
    !!order.errorConditionCode ||
    !!order.errorLocation ||
    !!order.acknowledgementCode;

  return (
    <section className={s.notice} role="alert">
      <AlertTriangle className={s.icon} size={20} aria-hidden="true" />
      <div className={s.body}>
        <h2 className={s.title}>The receiving system rejected this order</h2>
        <p className={s.text}>
          {order.errorConditionText ||
            "No explanation was returned. Place a new order, and report this if it happens again."}
        </p>

        {!!hasTechnicalDetail && (
          <details className={s.technical}>
            <summary className={s.technicalSummary}>Technical details</summary>
            <dl className={s.technicalList}>
              {!!order.errorConditionCode && (
                <div className={s.technicalRow}>
                  <dt>Error code</dt>
                  <dd>{order.errorConditionCode}</dd>
                </div>
              )}
              {!!order.errorLocation && (
                <div className={s.technicalRow}>
                  <dt>Location</dt>
                  <dd>{order.errorLocation}</dd>
                </div>
              )}
              {!!order.acknowledgementCode && (
                <div className={s.technicalRow}>
                  <dt>Acknowledgement</dt>
                  <dd>{order.acknowledgementCode}</dd>
                </div>
              )}
            </dl>
          </details>
        )}
      </div>
    </section>
  );
};
